import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { missingBillService } from '../../services/missingBillService';
import {
  MISSING_TAGS, MISSING_TAG_LABELS, type MissingTag, type MissingTagRow, type TagCellRef,
} from '../../lib/missingBills';

export type MissingTagEditorProps = {
  cell: TagCellRef;
  current: MissingTagRow | null;
  /** `changed`: a tag was saved or cleared, so the tags need reloading. */
  onDone: (changed: boolean) => void;
  disabled?: boolean;
  /** Tells the board a save is in flight, so it can lock its own controls. */
  onSavingChange?: (saving: boolean) => void;
};

/** Tag one missing NMI-month, or clear its tag. Opened from a missing cell only. */
export const MissingTagEditor = ({ cell, current, onDone, disabled = false, onSavingChange }: MissingTagEditorProps) => {
  const [tag, setTag] = useState<MissingTag>(current?.tag ?? 'chasing_retailer');
  const [note, setNote] = useState(current?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locked = saving || disabled;

  useEffect(() => {
    onSavingChange?.(saving);
    return () => onSavingChange?.(false);
  }, [saving, onSavingChange]);

  // Focus moves into the editor on open; the board returns it to the Tag button on close.
  const selectRef = useRef<HTMLSelectElement>(null);
  useEffect(() => { selectRef.current?.focus(); }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape' || saving) return;
    e.preventDefault();
    e.stopPropagation();
    onDone(false);
  };

  const run = async (action: () => Promise<{ success: boolean; error?: string }>, failure: string) => {
    setSaving(true);
    setError(null);
    try {
      const res = await action();
      if (!res.success) { setError(res.error ?? failure); return; }
      onDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : failure);
    } finally {
      setSaving(false);
    }
  };

  const save = () => run(() => missingBillService.setTag([cell], tag, note.trim()), 'Could not save the tag');
  const clear = () => {
    if (!current) return Promise.resolve();
    return run(() => missingBillService.clearTags([current.id]), 'Could not clear the tag');
  };

  return (
    // Escape from any of its controls bubbles up here and closes the editor.
    <div data-testid="nmi-tag-editor" onKeyDown={onKeyDown} className="mt-2 w-56 space-y-1.5 rounded-lg border border-gray-200 bg-white p-2 shadow-sm whitespace-normal">
      <select
        ref={selectRef}
        aria-label="Follow-up tag"
        data-testid="nmi-tag-select"
        className="w-full rounded border border-gray-300 bg-white px-1.5 py-1 text-xs"
        value={tag}
        disabled={locked}
        onChange={(e) => setTag(e.target.value as MissingTag)}
      >
        {MISSING_TAGS.map((t) => <option key={t} value={t}>{MISSING_TAG_LABELS[t]}</option>)}
      </select>
      <textarea
        aria-label="Note"
        data-testid="nmi-tag-note"
        className="w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
        rows={2}
        placeholder="Note (optional)"
        value={note}
        disabled={locked}
        onChange={(e) => setNote(e.target.value)}
      />
      {error && <p data-testid="nmi-tag-error" className="text-[11px] text-red-600">{error}</p>}
      <div className="flex items-center gap-1.5">
        <Button data-testid="nmi-tag-save" size="sm" disabled={locked} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        {current && (
          <Button data-testid="nmi-tag-clear" variant="outline" size="sm" disabled={locked} onClick={() => void clear()}>
            Clear
          </Button>
        )}
        <Button data-testid="nmi-tag-cancel" variant="ghost" size="sm" disabled={saving} onClick={() => onDone(false)}>Cancel</Button>
      </div>
    </div>
  );
};
